import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Celebration, PlayArrow } from '@mui/icons-material';
import { useAuth } from '../../Context/AuthContext';
import Button from './ui/Button';
import GameControls from './ui/GameControls';
import ResultView from './ui/ResultView';
import { COLOR } from './ui/tokens';

/**
 * Snake / PuzzleMaster-style manual play phase machine.
 * Play loop is fully client-side; upload/leaderboard stay on the shell session.
 */
export function useManualPlay({ enablePause = true } = {}) {
    // menu | playing | paused | over | ended (a run the player ended by hand)
    const [phase, setPhase] = useState('menu');
    // { score, scoreLabel?, details? } of the run that was ended by hand.
    const [result, setResult] = useState(null);

    const startGame = useCallback(() => {
        setResult(null);
        setPhase('playing');
    }, []);
    /**
     * End / New Game. With a `result` ({ score }) the run's result screen is
     * shown (phase "ended"); without one the page goes back to the menu.
     * Saving is unchanged: a run ended by hand is not submitted.
     */
    const endGame = useCallback((endResult) => {
        if (endResult && typeof endResult === 'object' && !endResult.nativeEvent) {
            setResult(endResult);
            setPhase('ended');
            return;
        }
        setResult(null);
        setPhase('menu');
    }, []);
    const markOver = useCallback(() => setPhase('over'), []);

    const togglePause = useCallback(() => {
        if (!enablePause) return;
        setPhase((prev) => {
            if (prev === 'playing') return 'paused';
            if (prev === 'paused') return 'playing';
            return prev;
        });
    }, [enablePause]);

    const isLive = phase === 'playing';
    const isActive = phase === 'playing' || phase === 'paused' || phase === 'over';

    return useMemo(() => ({
        phase,
        setPhase,
        result,
        startGame,
        endGame,
        markOver,
        togglePause,
        isLive,
        isActive,
        enablePause,
    }), [phase, result, startGame, endGame, markOver, togglePause, isLive, isActive, enablePause]);
}

/** Overlay heading for the play-again screen. Wins can pass `overTitle` or `won`. */
export function overScreenTitle({ won = false, overTitle } = {}) {
    if (overTitle != null && String(overTitle).trim() !== '') return String(overTitle);
    return won ? 'You Won!' : 'Game Over';
}

/** True for anonymous visitors and guest accounts (their runs are not kept). */
export function isGuestUser(auth) {
    if (!auth || !auth.token) return true;
    const u = auth.user || {};
    return u.user_type === 'guest' || u.role === 'guest' || u.is_guest === true;
}

const KEY_START = new Set(['Enter', ' ']);

export function isEditableTarget(target) {
    if (!target || typeof target !== 'object') return false;
    const tag = String(target.tagName || '').toUpperCase();
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || Boolean(target.isContentEditable);
}

/**
 * Enter / Space starts from the keyboard while `active`. Skips form fields,
 * auto-repeated keys, and (for Space) presses outside the optional play area.
 * Games that use Enter or Space as a move key keep `active` false during play
 * (and pass false forever when those keys end a run and would restart it).
 */
export function useKeyboardStart({
    active,
    onStart,
    rootRef = null,
    enabled = true,
}) {
    useEffect(() => {
        if (!enabled || !active || typeof onStart !== 'function') return undefined;
        const onKeyDown = (e) => {
            if (!KEY_START.has(e.key) || e.defaultPrevented || e.repeat) return;
            if (isEditableTarget(e.target)) return;
            const root = rootRef && rootRef.current;
            const target = e.target;
            const onBody = !target || target === document.body || target === document.documentElement;
            const inRoot = Boolean(root && target && root.contains(target));
            if (root) {
                if (!onBody && !inRoot) return;
                if (inRoot && target !== root) return; // buttons inside handle their own keys
                if (e.key === ' ' && !inRoot) return;
            }
            e.preventDefault();
            onStart();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [active, onStart, rootRef, enabled]);
}

const CONFETTI_COLORS = ['#FF6B6B', '#4ECDC4', '#ffe066', '#ff6b9d', '#7dffd4', '#ffa116', '#c4b5fd'];

function ConfettiBurst() {
    const pieces = useMemo(() => Array.from({ length: 40 }, (_, i) => ({
        id: i,
        left: `${((i * 23) + 7) % 100}%`,
        delay: `${(i % 14) * 0.11}s`,
        duration: `${1.7 + (i % 6) * 0.28}s`,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        size: 5 + (i % 5) * 2,
        drift: (i % 2 === 0 ? 1 : -1) * (8 + (i % 5) * 3),
    })), []);
    return (
        <Box
            aria-hidden
            data-testid="play-confetti"
            sx={{
                position: 'absolute',
                inset: 0,
                overflow: 'hidden',
                pointerEvents: 'none',
                zIndex: 0,
                '@keyframes playConfettiFall': {
                    '0%': { transform: 'translate3d(0,-16px,0) rotate(0deg)', opacity: 1 },
                    '100%': { transform: 'translate3d(var(--drift), 460px, 0) rotate(540deg)', opacity: 0.12 },
                },
            }}
        >
            {pieces.map((p) => (
                <Box
                    key={p.id}
                    sx={{
                        position: 'absolute',
                        top: -10,
                        left: p.left,
                        width: p.size,
                        height: p.size * 0.55,
                        bgcolor: p.color,
                        borderRadius: '1px',
                        '--drift': `${p.drift}px`,
                        animation: `playConfettiFall ${p.duration} linear infinite`,
                        animationDelay: p.delay,
                    }}
                />
            ))}
        </Box>
    );
}

/**
 * Overlay + control bar matching Snake Start Game / Pause / End Game UX.
 */
export function ManualPlayChrome({
    phase,
    onStart,
    onPause,
    onEnd,
    accentColor = COLOR.primary,
    showPause = true,
    controlsHint,
    children,
    minHeight = 280,
    /** Optional UI on the Start / Play Again overlays (e.g. per-run config). */
    setupContent = null,
    /** Optional in-run overlay (e.g. between-level announcement). Other games can ignore it. */
    bannerContent = null,
    /** Campaign / round win — shows confetti and a win title instead of Game Over. */
    won = false,
    /** Overrides the over-screen heading. Defaults to Game Over, or You Won! when `won`. */
    overTitle,
    /** Extra nodes on the over overlay (rewards, score line). */
    overContent = null,
    /**
     * When false, hide the overlay "Play Again" button and keep only the lower
     * chrome / shell control (avoids duplicate CTAs with setupContent on over).
     */
    showOverlayPlayAgain = true,
    /** When false, the over overlay is a plain dark panel (no backdrop blur). */
    overBlur = true,
    /** `play.result` from useManualPlay: the run ended with End / New Game. */
    result = null,
    /** Label for the End button (some games call it New Game). */
    endLabel = 'End Game',
    /**
     * Enter / Space start from the keyboard. Games that use Enter or Space as
     * a move key pass false, so the key that ended a run cannot restart it.
     */
    keyboardStart = true,
}) {
    const auth = useAuth();
    const guest = isGuestUser(auth);
    const rootRef = useRef(null);

    // Keyboard Start: Enter (or Space on the play area) starts from the menu or
    // a result screen, unless focus is in a form field or another control.
    const startActive = (
        keyboardStart
        && (phase === 'menu' || phase === 'ended' || phase === 'over')
        && !(phase === 'over' && !showOverlayPlayAgain)
    );
    useKeyboardStart({
        active: startActive,
        onStart,
        rootRef,
        enabled: keyboardStart,
    });

    return (
        <Box>
            <Box
                ref={rootRef}
                tabIndex={0}
                data-testid="play-area"
                aria-label="Game area"
                sx={{
                    '&:focus-visible': { outline: 'none', boxShadow: `0 0 0 3px ${COLOR.focus}` },
                    position: 'relative',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.08)',
                    bgcolor: 'rgba(0,0,0,0.25)',
                    minHeight,
                }}
            >
                {children}

                {phase === 'menu' && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            bgcolor: 'rgba(0,0,0,0.78)',
                            backdropFilter: 'blur(4px)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1.5,
                            zIndex: 10,
                            p: 2,
                            overflowY: 'auto',
                        }}
                    >
                        {setupContent}
                        <Button size="lg" onClick={onStart} startIcon={<PlayArrow />}>
                            Start Game
                        </Button>
                        {controlsHint ? (
                            <Typography variant="caption" sx={{ color: COLOR.textMuted, textAlign: 'center', maxWidth: 360 }}>
                                {controlsHint}
                            </Typography>
                        ) : null}
                    </Box>
                )}

                {phase === 'ended' && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            bgcolor: 'rgba(0,0,0,0.8)',
                            backdropFilter: 'blur(4px)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1.5,
                            zIndex: 10,
                            p: 2,
                            overflowY: 'auto',
                        }}
                    >
                        <ResultView
                            title={(result && result.title) || 'Run ended'}
                            score={result ? result.score : null}
                            scoreLabel={(result && result.scoreLabel) || 'Score'}
                            details={result ? result.details : null}
                            endedManually
                            isGuest={guest}
                            accentColor={accentColor}
                            onPlayAgain={onStart}
                        />
                        {setupContent ? <Box sx={{ zIndex: 1 }}>{setupContent}</Box> : null}
                    </Box>
                )}

                {bannerContent && phase !== 'menu' && phase !== 'over' && phase !== 'ended' && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            bgcolor: 'rgba(0,0,0,0.72)',
                            backdropFilter: 'blur(4px)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1.5,
                            zIndex: 9,
                            p: 2,
                            overflowY: 'auto',
                        }}
                    >
                        {bannerContent}
                    </Box>
                )}

                {phase === 'paused' && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            bgcolor: 'rgba(0,0,0,0.55)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10,
                        }}
                    >
                        <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
                            Paused
                        </Typography>
                    </Box>
                )}

                {phase === 'over' && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            bgcolor: won ? 'rgba(8, 24, 16, 0.78)' : 'rgba(0,0,0,0.72)',
                            ...(overBlur ? { backdropFilter: 'blur(4px)' } : {}),
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1.5,
                            zIndex: 10,
                            p: 2,
                            overflowY: 'auto',
                        }}
                        aria-label={won ? 'Game won' : 'Game over'}
                    >
                        {won && <ConfettiBurst />}
                        {won && (
                            <Celebration
                                data-testid="play-confetti-icon"
                                sx={{ fontSize: 52, color: '#ffe066', zIndex: 1, filter: 'drop-shadow(0 0 10px rgba(255,224,102,0.55))' }}
                            />
                        )}
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, zIndex: 1 }}>
                            {overScreenTitle({ won, overTitle })}
                        </Typography>
                        {overContent ? <Box sx={{ zIndex: 1 }}>{overContent}</Box> : null}
                        {setupContent ? <Box sx={{ zIndex: 1 }}>{setupContent}</Box> : null}
                        {showOverlayPlayAgain ? (
                            <Button onClick={onStart} startIcon={<PlayArrow />} autoFocus sx={{ zIndex: 1 }}>
                                Play Again
                            </Button>
                        ) : null}
                    </Box>
                )}
            </Box>

            {/* One Play Again only: the overlay owns it when it shows one. */}
            {(phase === 'ended' || (phase === 'over' && showOverlayPlayAgain)) ? (
                <GameControls phase="menu" readyText={phase === 'ended' ? 'Run ended' : 'Game over'} />
            ) : (
                <GameControls
                    phase={phase}
                    onStart={onStart}
                    onPause={onPause}
                    onEnd={onEnd}
                    showPause={showPause}
                    endLabel={endLabel}
                />
            )}

            {controlsHint && phase !== 'menu' ? (
                <Typography variant="caption" sx={{ color: COLOR.textSubtle, display: 'block', mt: 1, textAlign: 'center' }}>
                    {controlsHint}
                </Typography>
            ) : null}
        </Box>
    );
}

export const GAME_KEYS = new Set([' ', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

/**
 * Stop Space and the arrow keys from scrolling the page while `active`
 * (for games with their own key handler instead of usePlayKeyboard).
 */
export function useGameKeyGuard(active) {
    useEffect(() => {
        if (!active) return undefined;
        const onKeyDown = (e) => {
            if (GAME_KEYS.has(e.key) && !isEditableTarget(e.target)) e.preventDefault();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [active]);
}

/**
 * Escape / Space pause helpers + custom key handler while playing.
 */
export function usePlayKeyboard({
    phase,
    togglePause,
    enablePause = true,
    onKey,
    deps = [],
}) {
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Space and the arrows must steer the game, not scroll the page.
            if ((phase === 'playing' || phase === 'paused') && GAME_KEYS.has(e.key) && !isEditableTarget(e.target)) {
                e.preventDefault();
            }
            if (enablePause && (phase === 'playing' || phase === 'paused') && e.key === 'Escape') {
                togglePause();
                e.preventDefault();
                return;
            }
            if (enablePause && phase === 'paused' && e.key === ' ') {
                togglePause();
                e.preventDefault();
                return;
            }
            if (phase !== 'playing') return;
            if (typeof onKey === 'function') onKey(e);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase, togglePause, enablePause, onKey, ...deps]);
}
